import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import * as Avatar from '@radix-ui/react-avatar';
import Link from '@dashboard/components/Link';
import clsx from 'clsx';
import Logo from '@dashboard/components/Logo';
import Meta from '@dashboard/components/Meta';
import { Button } from '@dashboard/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@dashboard/components/ui/select';
import {
  AuthFieldError,
  AuthFooter,
  AuthHeader,
  authButtonClassName,
  authInputClassName,
  authLink,
} from '@dashboard/components/AuthForm';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import AuthLayout from '@dashboard/layouts/AuthLayout';
import { apiUrl } from '@dashboard/services/env';
import { getImageSrcAndSrcSet } from '@/dashboard/services/getUrlFromCdn';
import { Check, ShieldCheck, TriangleAlert } from 'lucide-react';

import ClientMark from './ClientMark';
import { describeScopes } from './describeScopes';
import { isSafeRedirectUri } from './redirectUri';

function getMcpOAuthOrigin() {
  return new URL(apiUrl).origin;
}

/**
 * The Framer plugin's redirect_uri is our own relay callback, so the code can
 * be delivered with a background request and the popup closed from here,
 * instead of navigating to an intermediate "you can close this window" page.
 * Returns false for every other client (real navigation required) or when the
 * relay request fails, so the caller falls back to the normal redirect.
 */
async function deliverToFramerRelay(redirectUrl) {
  if (!redirectUrl.startsWith(`${getMcpOAuthOrigin()}/oauth/framer/callback`)) {
    return false;
  }
  try {
    const response = await fetch(redirectUrl);
    return response.ok;
  } catch {
    return false;
  }
}

function ConnectMarks({ clientId, clientName, tone = 'default' }) {
  return (
    <div className="mb-6 flex items-center gap-2.5" aria-hidden="true">
      <span className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-gray-200 dark:border-border bg-white dark:bg-card text-foreground">
        <Logo size={20} />
      </span>
      {tone === 'danger' ? (
        <span className="flex h-11 w-11 items-center justify-center rounded-[10px] bg-red-50 dark:bg-red-500/15 text-red-600 dark:text-red-400">
          <TriangleAlert className="size-5" aria-hidden="true" />
        </span>
      ) : (
        <>
          <span className="h-px w-5 bg-gray-300 dark:bg-border" />
          <ClientMark clientId={clientId} clientName={clientName} />
        </>
      )}
    </div>
  );
}

function PermissionList({ clientDisplayName, permissions }) {
  return (
    <>
      <p className="text-sm font-medium text-gray-900 dark:text-foreground">
        {clientDisplayName} will be able to
      </p>
      <ul className="mt-3 space-y-2.5 text-[15px] text-gray-600 dark:text-muted-foreground">
        {permissions.map(permission => (
          <li key={permission} className="flex items-start gap-2.5">
            <Check
              className="mt-0.5 size-5 shrink-0 text-gray-900 dark:text-foreground"
              aria-hidden="true"
            />
            <span>{permission}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

function StatusScreen({ title, description, clientId, clientName, tone, children }) {
  return (
    <AuthLayout>
      <ConnectMarks clientId={clientId} clientName={clientName} tone={tone} />
      <AuthHeader title={title} description={description} />
      {children}
    </AuthLayout>
  );
}

function StoreAvatar({ store, className = 'h-7 w-7 rounded-full' }) {
  const width = 28;
  const height = 28;
  const logo = getImageSrcAndSrcSet({
    url: store?.logo?.url,
    width,
    height,
  });

  return (
    <Avatar.Root className={`${className} overflow-hidden border border-border bg-muted`}>
      {logo ? (
        <Avatar.Image
          src={logo.src}
          srcSet={logo.srcSet}
          alt={store?.displayName || store?.name}
          className="h-full w-full object-cover"
          width={width}
          height={height}
        />
      ) : (
        <Avatar.Fallback
          className={`flex h-full w-full items-center justify-center uppercase text-xs font-bold bg-muted text-foreground`}
        >
          {(store.displayName || store.name)?.charAt(0)}
        </Avatar.Fallback>
      )}
    </Avatar.Root>
  );
}

export default function McpOAuthAuthorize() {
  const { isAuth, isAuthenticating, token, availableStores, user, refreshAvailableStores } =
    useAuth();
  const location = useLocation();
  const [selectedStore, setSelectedStore] = useState(null);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [error, setError] = useState(null);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [redirectedTo, setRedirectedTo] = useState(null);
  const [isDenied, setIsDenied] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const oauthParams = {
    client_id: searchParams.get('client_id'),
    client_name: searchParams.get('client_name'),
    redirect_uri: searchParams.get('redirect_uri'),
    scope: searchParams.get('scope'),
    state: searchParams.get('state'),
    code_challenge: searchParams.get('code_challenge'),
    code_challenge_method: searchParams.get('code_challenge_method'),
  };

  const clientDisplayName = oauthParams.client_name || oauthParams.client_id;
  const permissions = useMemo(() => describeScopes(oauthParams.scope), [oauthParams.scope]);
  const selectedStoreData = useMemo(
    () => availableStores?.find(store => store._id === selectedStore) || null,
    [availableStores, selectedStore],
  );
  const loginRedirect = `/login?redirect=/oauth/authorize&${searchParams.toString()}`;
  const signupRedirect = `/signup?redirect=/oauth/authorize&${searchParams.toString()}`;

  useEffect(() => {
    if (isAuth && availableStores?.length === 0 && !isLoadingStores) {
      setIsLoadingStores(true);
      refreshAvailableStores().finally(() => {
        setIsLoadingStores(false);
      });
    }
  }, [isAuth, availableStores, isLoadingStores, refreshAvailableStores]);

  if (!oauthParams.client_id || !oauthParams.redirect_uri) {
    return (
      <>
        <Meta meta={{ title: 'Invalid request - Checkout Page' }} />
        <StatusScreen
          title="Invalid request"
          description="We couldn't start this connection because the OAuth request is missing required parameters."
          tone="danger"
        />
      </>
    );
  }

  const handleAuthorize = async () => {
    if (!selectedStore) {
      setError('Please select a store to authorize');
      return;
    }

    setIsAuthorizing(true);
    setError(null);

    try {
      const response = await fetch(`${getMcpOAuthOrigin()}/oauth/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': token,
        },
        body: JSON.stringify({
          client_id: oauthParams.client_id,
          redirect_uri: oauthParams.redirect_uri,
          scope: oauthParams.scope,
          state: oauthParams.state,
          code_challenge: oauthParams.code_challenge,
          code_challenge_method: oauthParams.code_challenge_method,
          sellerId: selectedStore,
        }),
      });

      const json = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(json.data?.error_description || json.data?.error || 'Authorization failed');
      }

      const { data } = json;

      if (data.redirectUrl) {
        if (!isSafeRedirectUri(data.redirectUrl)) {
          throw new Error('This app has an invalid redirect address.');
        }
        if (await deliverToFramerRelay(data.redirectUrl)) {
          // Show the branded success card for a beat, then close the popup.
          // The card stays up if the browser refuses the scripted close.
          setRedirectedTo(data.redirectUrl);
          setTimeout(() => window.close(), 1500);
          return;
        }
        window.location.href = data.redirectUrl;
        setRedirectedTo(data.redirectUrl);
      } else {
        throw new Error('No redirect URL returned');
      }
    } catch (err) {
      setError(err.message || 'Failed to authorize. Please try again.');
      setIsAuthorizing(false);
    }
  };

  const handleDeny = async () => {
    if (!isSafeRedirectUri(oauthParams.redirect_uri)) {
      setIsDenied(true);
      return;
    }
    try {
      const url = new URL(oauthParams.redirect_uri);
      url.searchParams.set('error', 'access_denied');
      if (oauthParams.state) {
        url.searchParams.set('state', oauthParams.state);
      }
      if (await deliverToFramerRelay(url.toString())) {
        setIsDenied(true);
        setTimeout(() => window.close(), 1500);
        return;
      }
      window.location.href = url.toString();
    } catch {
      setIsDenied(true);
    }
  };

  if (isDenied) {
    return (
      <>
        <Meta meta={{ title: 'Authorization cancelled - Checkout Page' }} />
        <StatusScreen
          title="Authorization cancelled"
          description={`You declined to connect ${clientDisplayName}. You can close this tab or head back to your dashboard.`}
          clientId={oauthParams.client_id}
          clientName={oauthParams.client_name}
        >
          <Link to="/dashboard" bare className={authLink}>
            Go to dashboard
          </Link>
        </StatusScreen>
      </>
    );
  }

  if (redirectedTo) {
    return (
      <>
        <Meta meta={{ title: 'Connected - Checkout Page' }} />
        <StatusScreen
          title="Checkout Page connected"
          description={`Successfully connected. Head back to ${clientDisplayName} to continue.`}
          clientId={oauthParams.client_id}
          clientName={oauthParams.client_name}
        />
      </>
    );
  }

  if (isAuthenticating) {
    return (
      <>
        <Meta meta={{ title: 'Loading connection - Checkout Page' }} />
        <StatusScreen
          title="Loading connection"
          description="Checking your account and available stores."
          clientId={oauthParams.client_id}
          clientName={oauthParams.client_name}
        />
      </>
    );
  }

  const cancelButton = (
    <p className="mt-4 text-center text-sm">
      <button type="button" onClick={handleDeny} disabled={isAuthorizing} className={authLink}>
        Cancel
      </button>
    </p>
  );

  if (!isAuth) {
    return (
      <>
        <Meta meta={{ title: `Connect Checkout Page to ${clientDisplayName}` }} />
        <AuthLayout>
          <ConnectMarks clientId={oauthParams.client_id} clientName={oauthParams.client_name} />
          <AuthHeader
            title={`Connect Checkout Page to ${clientDisplayName}`}
            description={`Log in or create a free account, then choose the store ${clientDisplayName} can use.`}
          />

          <PermissionList clientDisplayName={clientDisplayName} permissions={permissions} />

          <Button to={loginRedirect} className={clsx(authButtonClassName, 'mt-7 w-full')}>
            Log in to connect
          </Button>
          <Button
            to={signupRedirect}
            variant="outline"
            className={clsx(authButtonClassName, 'mt-3 w-full')}
          >
            Create a free account
          </Button>
          <p className="mt-3 text-center text-sm text-gray-600 dark:text-muted-foreground">
            New to Checkout Page? Free up to $3K in monthly sales.
          </p>
          {cancelButton}
        </AuthLayout>
      </>
    );
  }

  return (
    <>
      <Meta meta={{ title: `Connect Checkout Page to ${clientDisplayName}` }} />
      <AuthLayout>
        <ConnectMarks clientId={oauthParams.client_id} clientName={oauthParams.client_name} />
        <AuthHeader
          title={`Connect Checkout Page to ${clientDisplayName}`}
          description={`${clientDisplayName} gets access to the store you choose.`}
        />

        <PermissionList clientDisplayName={clientDisplayName} permissions={permissions} />

        <div className="mt-7">
          <label
            htmlFor="mcp-oauth-store"
            className="mb-1.5 block text-sm font-medium text-gray-900 dark:text-foreground"
          >
            Store
          </label>

          {isLoadingStores ? (
            <div
              className={clsx(
                authInputClassName,
                'flex items-center border border-gray-200 dark:border-border text-sm text-muted-foreground',
              )}
            >
              Loading stores...
            </div>
          ) : availableStores?.length > 0 ? (
            <Select
              value={selectedStore || undefined}
              onValueChange={value => setSelectedStore(value)}
            >
              <SelectTrigger
                id="mcp-oauth-store"
                className={clsx(
                  authInputClassName,
                  'w-full border-gray-200 dark:border-border bg-white dark:bg-background text-left text-[15px] text-foreground shadow-none data-[size=default]:h-11',
                )}
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  {selectedStoreData ? (
                    <>
                      <StoreAvatar store={selectedStoreData} className="h-6 w-6 rounded-full" />
                      <span className="truncate">
                        {selectedStoreData.displayName ||
                          selectedStoreData.name ||
                          selectedStoreData.slug ||
                          'Your new store'}
                      </span>
                    </>
                  ) : (
                    <span className="truncate text-muted-foreground">Choose a store</span>
                  )}
                </div>
              </SelectTrigger>
              <SelectContent className="rounded-[10px] p-1">
                {availableStores.map(store => (
                  <SelectItem key={store._id} value={store._id} className="rounded-md px-2.5 py-2">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <StoreAvatar store={store} />
                      <div className="min-w-0">
                        <div className="truncate font-medium text-foreground">
                          {store.displayName || store.name || store.slug || 'Your new store'}
                        </div>
                        {store.slug ? (
                          <div className="truncate text-xs text-muted-foreground">
                            {store.slug}.checkoutpage.com
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <AuthFieldError>
              No stores available yet. Create a store first, then come back to finish connecting.
            </AuthFieldError>
          )}
        </div>

        {error ? (
          <div className="mt-4">
            <AuthFieldError>{error}</AuthFieldError>
          </div>
        ) : null}

        <Button
          onClick={handleAuthorize}
          disabled={!selectedStore || isAuthorizing}
          className={clsx(authButtonClassName, 'mt-6 w-full')}
        >
          {isAuthorizing ? 'Connecting...' : 'Connect Checkout Page'}
        </Button>
        {cancelButton}

        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-gray-500 dark:text-muted-foreground">
          <ShieldCheck className="size-4 shrink-0" aria-hidden="true" />
          You can revoke access anytime from your account settings.
        </p>

        <AuthFooter>
          Signed in as{' '}
          <span className="font-medium text-gray-900 dark:text-foreground">{user?.email}</span>.{' '}
          <Link href={loginRedirect} bare className={clsx(authLink, 'whitespace-nowrap')}>
            Switch account
          </Link>
        </AuthFooter>
      </AuthLayout>
    </>
  );
}
